import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { TagManager } from "@dealer-center/core-third-party-gtm";
import { useGetUser } from "@dealer-center/ddc-api-user-fetch";
import { useControlcenterAPIInterface } from "@dealer-center/ddc-pages-controlcenter-fetch";
import { MessagePage } from "@dealer-center/core-custom-components";
import { ControlCenterContainer } from "./components/ControlCenterContainer";
import { Locale } from "@dealer-center/ddc-core-utils";
import LABELS from "./labels.json";

// Relative to <base href="/landing/"> — resolves to /landing/dc-favicon.ico in both dev and prod.
// Do not use an absolute path (/dc-favicon.ico) as that would bypass the app's baseHref.
const DC_FAVICON_PATH = "dc-favicon.ico";

export const PagesControlcenterApp = () => {
  const location = useLocation();
  const userState = useGetUser();
  const [locale, setLocale] = useState("en-US");
  const { data: userEntity } = userState;

  // Apply the default "ControlCenter" title once per route, during render (not
  // in an effect), so a child page's own title effect reliably wins. Two
  // reasons this is render-time and pathname-guarded:
  //   1. When the /cc/* guard pre-warms the ccUserInfo cache this component
  //      renders <Outlet /> on its first commit, so parent and child mount in
  //      the same commit. Effects fire bottom-up, so a parent title *effect*
  //      would run after — and clobber — the child's. Render runs top-down,
  //      before any child effect, so the child's title wins.
  //   2. Guarding on pathname means we set the default only when the route
  //      changes, not on every incidental parent re-render (user data loading,
  //      locale sync, query refresh). Those re-renders don't remount the child,
  //      so its []-dep title effect would not re-fire to restore its title —
  //      an unguarded assignment would leave the child page showing
  //      "ControlCenter". On real navigation the pathname changes, the default
  //      is re-applied in render, and the newly mounted child's effect overrides
  //      it again. Pages that set no title keep the default.
  const titledPathnameRef = useRef<string | null>(null);
  if (titledPathnameRef.current !== location.pathname) {
    titledPathnameRef.current = location.pathname;
    document.title = "ControlCenter";
  }

  useEffect(() => {
    const linkEl =
      document.querySelector<HTMLLinkElement>("link[rel='icon']") ??
      (() => {
        const el = document.createElement("link");
        el.rel = "icon";
        document.head.appendChild(el);
        return el;
      })();
    const originalHref = linkEl.href;
    linkEl.href = DC_FAVICON_PATH;
    return () => {
      linkEl.href = originalHref;
    };
  }, []);

  useEffect(() => {
    const newLocale = Locale.getLocale();

    if (locale !== newLocale) {
      Locale.change(newLocale);
      setLocale(newLocale);
    }
  }, [locale]);

  useEffect(() => {
    TagManager.event({
      eventName: "page_view"
    });
  }, [location]);

  const { asAccount, asUser } = useParams();

  const { data: ccUserInfo } = useControlcenterAPIInterface({
    asAccount,
    asUser
  });

  // Load labels synchronously during render (not in an effect) so they are set
  // before any child route renders and calls Locale.getText. When the shared
  // ccUserInfo query cache is pre-warmed (e.g. by StrictlyInternalGuard), this
  // component renders <Outlet /> on its first commit, so a label-loading effect
  // would fire too late and children would show raw label keys.
  Locale.setLabelsOnce(LABELS);

  if (!ccUserInfo) {
    return (
      <div data-testid="ddc-page">
        <MessagePage
          actionName={"Loading user permissions"}
          message="Loading user permissions"
        />
      </div>
    );
  }
  return (
    <ControlCenterContainer>
      <Outlet context={{ ccUserInfo, userEntity }} />
    </ControlCenterContainer>
  );
};
